import { FormEvent } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { AuthShell } from "@/components/AuthShell"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

import type { PageProps } from "@/types/inertia"

type Props = { token: string; name: string; email: string; invited_by?: string | null }

export default function InviteAccept({ token, name, email, invited_by }: Props) {
  const { props } = usePage<PageProps<Props>>()
  const form = useForm({ name, password: "" })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.patch(`/invite/${token}`)
  }

  const errors = props.errors ?? {}

  return (
    <>
      <Head title="Accept your invite">
        <meta name="description" content="Set your password to activate your FloorCore Measure account." />
        <meta property="og:title" content="Accept your invite" />
        <meta property="og:description" content="Set your password to activate your FloorCore Measure account." />
      </Head>
      <AuthShell
        footer={
          <>
            This invite link expires in 7 days. Ask your admin to resend it if it
            lapses.
          </>
        }
      >
        {invited_by && (
          <span className="badge badge-accent uppercase tracking-wider">
            Invited by {invited_by}
          </span>
        )}
        <h2 className={`auth-title ${invited_by ? "mt-3" : ""}`}>
          Set your password
        </h2>
        <p className="mt-1.5 text-sm text-ink-muted">
          Activating the account for{" "}
          <strong className="font-mono text-xs">{email}</strong>.
        </p>

        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="name">Your name</label>
            <Input
              id="name"
              type="text"
              autoComplete="name"
              required
              aria-invalid={!!errors.name}
              value={form.data.name}
              onChange={(e) => form.setData("name", e.target.value)}
            />
            {errors.name && (
              <p className="text-xs text-danger-display">{errors.name}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <label htmlFor="password">Password</label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              placeholder="At least 12 characters"
              aria-invalid={!!errors.password}
              value={form.data.password}
              onChange={(e) => form.setData("password", e.target.value)}
            />
            {errors.password && (
              <p className="text-xs text-danger-display">{errors.password}</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={form.processing}>
            Activate account
          </Button>
        </form>
      </AuthShell>
    </>
  )
}
